import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import {
  ResumeDetails,
  selectors as resumeDistributionSelectors,
  actions as resumeDistributionActions,
} from 'builder/modules/resumeDistribution'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { actions as resumeReviewActions } from 'builder/modules/resumeReview'
import {
  selectors as panelSelectors,
  actions as panelActions,
  BriefResume,
} from 'builder/modules/panel'
import { actions as renderingActions } from 'builder/modules/rendering'
import { DocumentTypes as TYPES, FormatType } from 'builder/modules/constants'
import { ERDSteps } from '../../types'
import FormLayout from '../../components/FormLayout'
import PersonalDetailsForm from '../../components/PersonalDetailsForm'
import { JobPreferencesForm } from '../../components/JobPreferencesForm/JobPreferencesForm'

const ResumeDistributionEditView = () => {
  const dispatch = useDispatch()
  const [query] = useSearchParams()
  const resumeId = query.get('resume_id')
  const documentType = query.get('document_type')
  const documentFormat = query.get('document_format')
  const documentTemplate = query.get('document_template')
  const location = useLocation()
  const navigate = useNavigate()

  const [activeStep, setActiveStep] = useState<ERDSteps>(ERDSteps.FORM_STEP_1)
  const selectedResumeDetails = useTypedSelector<ResumeDetails>(
    resumeDistributionSelectors.selectedResumeDetails,
  )
  const documentsList = useTypedSelector(panelSelectors.documents)
  const [selectedResume, setSelectedResume] = useState<BriefResume | null>(null)

  useEffect(() => {
    dispatch(panelActions.fetchDocuments())
  }, [dispatch])

  useEffect(() => {
    if (resumeId && documentType) {
      dispatch(
        renderingActions.download({
          id: Number(resumeId),
          type: documentType as TYPES,
          format: documentFormat as FormatType,
          template: documentTemplate as string,
        }),
      )

      if (documentType !== TYPES.coverLetter)
        dispatch(resumeReviewActions.sendResume({ resumeId: Number(resumeId) }))
      navigate(`${location.pathname}?resume_id=${resumeId}`, { replace: true })
    }
  }, [documentType, documentFormat, documentTemplate, resumeId, location, dispatch])

  useEffect(() => {
    if (!selectedResume && documentsList.length && resumeId) {
      const resume = documentsList.find(document => document.id === Number(resumeId))
      setSelectedResume(resume as BriefResume)
      dispatch(resumeDistributionActions.setResumeDetails(resume as BriefResume))
    }
  }, [documentsList, selectedResume, resumeId, dispatch])

  switch (activeStep) {
    case ERDSteps.FORM_STEP_1:
      return (
        <FormLayout activeStep={activeStep}>
          <PersonalDetailsForm
            resumePayload={selectedResumeDetails || selectedResume}
            setActiveStep={setActiveStep}
            setSelectedResumeDetails={(resumeDetails: ResumeDetails) => {
              dispatch(
                resumeDistributionActions.setPersonalDetailsForm({
                  firstName: '',
                  lastName: '',
                  email: '',
                  jobTitle: '',
                  linkedin: '',
                  fullAddress: '',
                }),
              )
              if (resumeDetails) {
                dispatch(resumeDistributionActions.setResumeDetails(resumeDetails))
              }
            }}
          />
        </FormLayout>
      )
    case ERDSteps.FORM_STEP_2:
      return (
        <FormLayout activeStep={activeStep}>
          <JobPreferencesForm setActiveStep={setActiveStep} />
        </FormLayout>
      )
    default:
      return <></>
  }
}

export default ResumeDistributionEditView
